import { useHandler } from "@redotech/react-util/hook";
import { getAvailableTimezones } from "@redotech/redo-common-model/timezone-utils";
import {
  RcoDatetimeRangeSegmentSchema,
  rcoDatetimeRangeSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { TimePicker } from "../time-picker";

export const DatetimeRangeInspectorContent = memo(
  function DatetimeRangeInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoDatetimeRangeSegmentSchema.parse(step.segment),
      [step],
    );

    const timezoneDisplay = segment.ianaTimezone.replace(/_/g, " ");

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t(
            "Current time is between {{startDateTime}} and {{endDateTime}} in timezone {{timezone}}",
            {
              startDateTime: segment.startDateTime.toLocaleString(),
              endDateTime: segment.endDateTime.toLocaleString(),
              timezone: timezoneDisplay,
            },
          )}
        </Text>
      </Flex>
    );
  },
);

export const EditableDatetimeRangeInspectorContent = memo(
  function EditableDatetimeRangeInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoDatetimeRangeSegmentSchema.parse(step.segment),
      [step],
    );

    const availableTimezones = useMemo(() => getAvailableTimezones(), []);

    const timezoneOptions: TextRedoListItem<string>[] = useMemo(() => {
      return availableTimezones.map((timezone) => ({
        type: "text",
        id: timezone,
        value: timezone,
        text: timezone.replace(/_/g, " "),
      }));
    }, [availableTimezones]);

    const selectedTimezoneItem = useMemo(() => {
      return timezoneOptions.find(
        (option) => option.value === segment.ianaTimezone,
      );
    }, [timezoneOptions, segment.ianaTimezone]);

    const handleTimezoneSelected = useHandler(
      ({ item }: { item: RedoListItem<string> }) => {
        const updatedSegment = { ...segment, ianaTimezone: item.value };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const {
      startPlainDate,
      startTime,
      endPlainDate,
      endTime,
      handleStartPlainDateChange,
      handleStartTimeChange,
      handleEndPlainDateChange,
      handleEndTimeChange,
    } = useDateTimeRange({ segment, step, setStep });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Current time is between")}
          </Text>
        </Flex>
        <Flex align="flex-start" dir="column" gap="xs">
          <RedoSingleDatePicker
            buttonSize="sm"
            date={startPlainDate}
            setDate={(plainDate) => {
              handleStartPlainDateChange(plainDate);
            }}
          />
          <TimePicker
            hours={startTime.hours}
            minutes={startTime.minutes}
            setHours={(hours) =>
              handleStartTimeChange({ hours, minutes: startTime.minutes })
            }
            setMinutes={(minutes) =>
              handleStartTimeChange({ hours: startTime.hours, minutes })
            }
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("and")}
        </Text>
        <Flex align="flex-start" dir="column" gap="xs">
          <RedoSingleDatePicker
            buttonSize="sm"
            date={endPlainDate}
            setDate={(plainDate) => {
              handleEndPlainDateChange(plainDate);
            }}
          />
          <TimePicker
            hours={endTime.hours}
            minutes={endTime.minutes}
            setHours={(hours) =>
              handleEndTimeChange({ hours, minutes: endTime.minutes })
            }
            setMinutes={(minutes) =>
              handleEndTimeChange({ hours: endTime.hours, minutes })
            }
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("in timezone")}
        </Text>
        <RedoSingleSelectDropdownInput<string>
          fitToAnchor={false}
          options={timezoneOptions}
          optionSelected={handleTimezoneSelected}
          selectedItem={selectedTimezoneItem}
        />
      </>
    );
  },
);

function useDateTimeRange({
  segment,
  step,
  setStep,
}: {
  segment: RcoDatetimeRangeSegmentSchema;
  step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
    matchingNextId?: string | undefined;
    nonMatchingNextId?: string | undefined;
  };
  setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
}) {
  const startPlainDate = useMemo(() => {
    return Temporal.PlainDate.from(segment.startDateTime);
  }, [segment.startDateTime]);

  const startTime = useMemo(() => {
    const startPlainDateTime = Temporal.PlainDateTime.from(
      segment.startDateTime,
    );
    return {
      hours: startPlainDateTime.hour,
      minutes: startPlainDateTime.minute,
    };
  }, [segment.startDateTime]);

  const endPlainDate = useMemo(() => {
    return Temporal.PlainDate.from(segment.endDateTime);
  }, [segment.endDateTime]);

  const endTime = useMemo(() => {
    const endPlainDateTime = Temporal.PlainDateTime.from(segment.endDateTime);
    return { hours: endPlainDateTime.hour, minutes: endPlainDateTime.minute };
  }, [segment.endDateTime]);

  const handleStartPlainDateChange = useHandler(
    (startPlainDate: Temporal.PlainDate) => {
      const startDateTime = startPlainDate.toPlainDateTime(
        Temporal.PlainTime.from({
          hour: startTime.hours,
          minute: startTime.minutes,
        }),
      );
      const updatedSegment = {
        ...segment,
        startDateTime: startDateTime.toString(),
      };
      setStep({ ...step, segment: updatedSegment });
    },
  );

  const handleStartTimeChange = useHandler(
    (_startTime: { hours: number; minutes: number }) => {
      const startDateTime = startPlainDate.toPlainDateTime(
        Temporal.PlainTime.from({
          hour: _startTime.hours,
          minute: _startTime.minutes,
        }),
      );
      const updatedSegment = {
        ...segment,
        startDateTime: startDateTime.toString(),
      };
      setStep({ ...step, segment: updatedSegment });
    },
  );

  const handleEndPlainDateChange = useHandler(
    (endPlainDate: Temporal.PlainDate) => {
      const endDateTime = endPlainDate.toPlainDateTime(
        Temporal.PlainTime.from({
          hour: endTime.hours,
          minute: endTime.minutes,
        }),
      );
      const updatedSegment = {
        ...segment,
        endDateTime: endDateTime.toString(),
      };
      setStep({ ...step, segment: updatedSegment });
    },
  );

  const handleEndTimeChange = useHandler(
    (_endTime: { hours: number; minutes: number }) => {
      const endDateTime = endPlainDate.toPlainDateTime(
        Temporal.PlainTime.from({
          hour: _endTime.hours,
          minute: _endTime.minutes,
        }),
      );
      const updatedSegment = {
        ...segment,
        endDateTime: endDateTime.toString(),
      };
      setStep({ ...step, segment: updatedSegment });
    },
  );

  const value = useMemo(() => {
    return {
      startPlainDate,
      startTime,
      endPlainDate,
      endTime,
      handleStartPlainDateChange,
      handleStartTimeChange,
      handleEndPlainDateChange,
      handleEndTimeChange,
    };
  }, [
    startPlainDate,
    startTime,
    endPlainDate,
    endTime,
    handleStartPlainDateChange,
    handleStartTimeChange,
    handleEndPlainDateChange,
    handleEndTimeChange,
  ]);

  return value;
}
